// Fehler verständlich anzeigen: Meldung der API (deutsch) plus, wo es hilft, ein nächster Schritt.
import { Link } from "react-router-dom";
import { ApiFehler } from "../api";
import { Icon } from "./Icon";

export interface FehlerInfo {
  text: string;
  code: string;
  /** Link zum Beheben (z. B. Einstellungen) */
  link?: { label: string; to: string };
  /** vorübergehend: nochmal versuchen lohnt sich */
  voruebergehend: boolean;
}

/**
 * Close liefert Feldfehler verschachtelt, z. B. {"note": {"errors": [], "field-errors": {"str": "Input should be a valid string"}}}.
 * Daraus wird „note > str: Input should be a valid string“; mehrere Meldungen stehen mit „; “ getrennt.
 */
export function feldFehlerText(wert: unknown, pfad: string[] = []): string {
  const ort = pfad.join(" > ");
  if (wert === null || wert === undefined) return "";
  if (typeof wert === "string") return ort ? `${ort}: ${wert}` : wert;
  if (typeof wert === "number" || typeof wert === "boolean") return ort ? `${ort}: ${wert}` : String(wert);
  if (Array.isArray(wert)) return wert.map((x) => feldFehlerText(x, pfad)).filter(Boolean).join("; ");
  if (typeof wert === "object") {
    const teile: string[] = [];
    for (const [k, v] of Object.entries(wert as Record<string, unknown>)) {
      // „errors“ und „field-errors“ sind nur Behälter: der Name gehört nicht in den Pfad
      const weiter = k === "errors" || k === "field-errors" ? pfad : [...pfad, k];
      const t = feldFehlerText(v, weiter);
      if (t) teile.push(t);
    }
    return teile.join("; ");
  }
  return "";
}

export function fehlerInfo(e: unknown): FehlerInfo {
  if (e instanceof ApiFehler) {
    const details = e.details ?? {};
    switch (e.code) {
      case "keine_verbindung":
        return { code: e.code, text: "Keine Verbindung zum CRM-Server. Bitte Internet prüfen und nochmal versuchen.", voruebergehend: true };
      case "nicht_angemeldet":
        return { code: e.code, text: "Die Sitzung ist abgelaufen. Bitte neu anmelden.", voruebergehend: false };
      case "close_schluessel_fehlt":
        return {
          code: e.code,
          text: e.message || "Für dich ist kein eigener Close-Schlüssel hinterlegt. Ohne ihn schreibt das CRM nichts nach Close.",
          link: { label: "Schlüssel eintragen", to: "/einstellungen#close" },
          voruebergehend: false,
        };
      case "close_schreiben_gesperrt":
        return { code: e.code, text: `${e.message} Close wurde nicht geändert.`, voruebergehend: false };
      case "close_fehler": {
        const antwort = details.close_antwort as { error?: string; errors?: unknown; "field-errors"?: unknown } | string | undefined;
        let grund = "";
        if (typeof antwort === "string") grund = antwort;
        else if (antwort?.["field-errors"] && feldFehlerText(antwort["field-errors"])) grund = feldFehlerText(antwort["field-errors"]);
        else if (antwort?.error) grund = antwort.error;
        else if (antwort?.errors && feldFehlerText(antwort.errors)) grund = feldFehlerText(antwort.errors);
        return { code: e.code, text: `Close hat die Änderung abgelehnt${grund ? `: ${grund}` : "."}`, voruebergehend: false };
      }
      case "close_nicht_erreichbar":
      case "close_ratenlimit":
        return { code: e.code, text: e.message || "Close ist gerade nicht erreichbar. Bitte gleich nochmal versuchen.", voruebergehend: true };
      case "cursor_abgelaufen":
        return { code: e.code, text: "Die Trefferliste ist abgelaufen und wird neu geladen.", voruebergehend: true };
      case "nicht_gefunden":
        if (e.message === "Unbekannter API-Pfad.") {
          return { code: e.code, text: "Diese Funktion ist auf dem Server noch nicht verfügbar.", voruebergehend: false };
        }
        return { code: e.code, text: e.message || "Nicht gefunden.", voruebergehend: false };
      case "interner_fehler":
        return { code: e.code, text: "Unerwarteter Fehler auf dem Server. Bitte nochmal versuchen; wenn es bleibt, Dilsar Bescheid geben.", voruebergehend: true };
      case "telefonie_nicht_eingerichtet":
        return { code: e.code, text: e.message || "Telefonie ist noch nicht eingerichtet.", link: { label: "Einstellungen", to: "/einstellungen#telefonie" }, voruebergehend: false };
      default:
        if (e.status >= 500 && !e.message) return { code: e.code, text: `Serverfehler (${e.status}). Bitte nochmal versuchen.`, voruebergehend: true };
        return { code: e.code, text: e.message || "Unbekannter Fehler.", voruebergehend: e.status >= 500 };
    }
  }
  if (e instanceof Error) return { code: "fehler", text: e.message, voruebergehend: false };
  return { code: "fehler", text: "Unbekannter Fehler.", voruebergehend: false };
}

export function FehlerBox({ fehler, onNochmal, kompakt = false }: { fehler: unknown; onNochmal?: () => void; kompakt?: boolean }) {
  const info = fehlerInfo(fehler);
  return (
    <div className={`fehlerbox${kompakt ? " fehlerbox--kompakt" : ""}`} role="alert">
      <Icon name="warnung" groesse={16} />
      <span className="fehlerbox__text">{info.text}</span>
      {info.link ? (
        <Link className="fehlerbox__link" to={info.link.to}>
          {info.link.label}
        </Link>
      ) : null}
      {onNochmal ? (
        <button type="button" className="knopf knopf--klein" onClick={onNochmal}>
          Nochmal
        </button>
      ) : null}
    </div>
  );
}
